import { Button } from "@hortis/ui/button";
import { DatePicker, createDateLabel } from "@hortis/ui/date-picker";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { Calendar, ChevronDown } from "@hortis/ui/icons";
import { RichTextEditor } from "@hortis/ui/rich-text-editor";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  MaterialTypePicker,
  MaterialTypePickerLabel,
} from "src/components/pickers/material-type-picker";
import { SexPicker, SexPickerLabel } from "src/components/pickers/sex-picker";
import {
  StatusPicker,
  StatusPickerLabel,
} from "src/components/pickers/status-picker";
import {
  TagMultiPicker,
  TagMultiPickerLabel,
} from "src/components/pickers/tag-picker/tag-picker";
import { mention } from "src/components/rich-text-editor/mention";
import { useCollectionSiteSettings } from "src/utils/hooks/collection-site-from-url";
import { useAccessTokenBasic } from "src/utils/use-access-token";
import {
  MaterialGroup,
  MaterialSex,
  PlantMaterialStatus,
} from "../../../../../generated/graphql";
import { FormTextField } from "../../../../components/text-field/text-field";
import { LabeledSwitch } from "../labeled-switch";
import { PositionFields } from "./position-fields";
import { QuantityWeightField } from "./quantity-weight-field/quantity-weight-field";
import type { PlantMaterialData } from "./schema";

export const materialGroupChips = [
  { label: "Plant", value: MaterialGroup.Plant },
  { label: "Seed", value: MaterialGroup.Seed },
];

export const statusChips = [
  { label: "Present", value: PlantMaterialStatus.Present },
  { label: "Absent", value: PlantMaterialStatus.Absent },
  { label: "Transient", value: PlantMaterialStatus.Transient },
  { label: "Unknown", value: PlantMaterialStatus.Unknown },
];

export const materialSexOptions = [
  { label: "Agamosperm", value: MaterialSex.Agamosperm },
  { label: "Female", value: MaterialSex.Female },
  { label: "Hermaphrodite", value: MaterialSex.Hermaphrodite },
  { label: "Male", value: MaterialSex.Male },
  { label: "Unknown", value: MaterialSex.Unknown },
];

export interface PlantMaterialDataFieldsProps {
  formNamePrefix?: string;
  hidePositionFields?: boolean;
  hideObservationFields?: boolean;
}

type EditableFields = Omit<PlantMaterialData, "tempId">;
export type FormFields = {
  [Key in keyof EditableFields as `${string}${Key}`]: EditableFields[Key];
};

export const PlantMaterialDataFields = ({
  formNamePrefix = "",
  hidePositionFields,
}: PlantMaterialDataFieldsProps) => {
  const form = useFormContext<Partial<FormFields>>();
  const { getValues, watch, setValue } = useFormContext<FormFields>();
  const materialGroup = watch(`${formNamePrefix}materialGroup`);
  const accessToken = useAccessTokenBasic();
  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ from: "/_layout/site/$siteSlug" });

  useEffect(() => {
    if (materialGroup === MaterialGroup.Seed) {
      setValue(`${formNamePrefix}massPlanting`, false);
    }
  }, [materialGroup, formNamePrefix, setValue]);

  const statusOption = watch(`${formNamePrefix}status`);
  const hideDateRemovedField = useMemo(() => {
    return statusOption !== PlantMaterialStatus.Absent;
  }, [statusOption]);

  useEffect(() => {
    if (hideDateRemovedField) {
      setValue(`${formNamePrefix}firstAbsent`, null);
    } else if (getValues(`${formNamePrefix}firstAbsent`) == null) {
      setValue(`${formNamePrefix}firstAbsent`, new Date());
    }
  }, [hideDateRemovedField, getValues, setValue, formNamePrefix]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        <LabeledSwitch
          name={`${formNamePrefix}materialQualifier`}
          primaryLabel="Material number"
        />
        <FormField
          control={form.control}
          name={`${formNamePrefix}firstPresent`}
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Date added</FormLabel>
              <FormControl>
                <DatePicker
                  value={field.value}
                  onChange={field.onChange}
                  data-cy="date-added-picker"
                >
                  <Button endIcon={Calendar} className="justify-between">
                    {field.value == null
                      ? "Select date added..."
                      : createDateLabel(field.value)}
                  </Button>
                </DatePicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${formNamePrefix}materialGroup`}
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Type</FormLabel>
              <FormControl>
                <MaterialTypePicker {...field}>
                  <Button
                    className="min-h-[44px] justify-between truncate px-[14px]! py-2!"
                    endIcon={ChevronDown}
                  >
                    {field.value == null ? (
                      <span className=" text-grey-500">Select a type...</span>
                    ) : (
                      <MaterialTypePickerLabel type={field.value} />
                    )}
                  </Button>
                </MaterialTypePicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${formNamePrefix}status`}
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Status</FormLabel>
              <FormControl>
                <StatusPicker {...field}>
                  <Button
                    className="min-h-[44px] justify-between truncate px-[14px]! py-2!"
                    endIcon={ChevronDown}
                  >
                    {field.value == null ? (
                      <span className="text-grey-500">Select a status...</span>
                    ) : (
                      <StatusPickerLabel value={field.value} />
                    )}
                  </Button>
                </StatusPicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {!hideDateRemovedField && (
          <FormField
            control={form.control}
            name={`${formNamePrefix}firstAbsent`}
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Date removed</FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    data-cy="date-removed-picker"
                  >
                    <Button endIcon={Calendar} className="justify-between">
                      {field.value == null
                        ? "Select date removed..."
                        : createDateLabel(field.value)}
                    </Button>
                  </DatePicker>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <QuantityWeightField formNamePrefix={formNamePrefix} />

        <FormField
          control={form.control}
          name={`${formNamePrefix}sex`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sex</FormLabel>
              <FormControl>
                <SexPicker {...field}>
                  <Button
                    className="min-h-[44px] justify-between truncate px-[14px]! py-2!"
                    endIcon={ChevronDown}
                  >
                    {field.value == null ? (
                      <span className="text-grey-500">Select a sex...</span>
                    ) : (
                      <SexPickerLabel value={field.value} />
                    )}
                  </Button>
                </SexPicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormTextField
          fullWidth
          label="Tag number"
          testId="tagNumber"
          name={`${formNamePrefix}tagNumber`}
        />
        <FormField
          control={form.control}
          name={`${formNamePrefix}tags`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <FormControl>
                <TagMultiPicker
                  tagLevel="site"
                  placeholder="Add tags..."
                  newTagOption
                  {...field}
                >
                  <Button
                    className="min-h-[44px] justify-between py-2! pl-3.5!"
                    endIcon={ChevronDown}
                    data-cy="material-tags"
                  >
                    {field.value == null || field.value.length === 0 ? (
                      <span className="text-grey-500">Add tags...</span>
                    ) : (
                      <TagMultiPickerLabel value={field.value} />
                    )}
                  </Button>
                </TagMultiPicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notesData"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                {accessToken != null && (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Add notes..."
                    data-cy="material-notes-input"
                    extensions={[mention({ accessToken, siteSlug, subdomain })]}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <PositionFields
        namePrefix={formNamePrefix}
        hideMap={hidePositionFields}
      />
    </div>
  );
};
